import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import UserSelect from "features/user-select/user-select";
import {
  updateAccountancyUserSettings,
  updateBoardMemberSettings,
  updateDirectorSettings,
  updateInvoiceApproverSettings,
} from "services/company.service";
import { fetchById as fetchUserById } from "services/user-select.service";
import { CompanyEntity } from "types/Company";
import { Badge } from "../badge";
import { saveErrorKey } from "../save-error";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";

export type Role = {
  /** The company's column, and the field the save request carries. */
  field:
    | "director"
    | "boardMember"
    | "accountancyUser"
    | "invoiceApproverUser"
    | "assetsResponsibleUser";
  labelKey: string;
  hintKey: string;
  pickerLabelKey: string;
  save: (
    body: Record<string, string | null>,
    companyId: string
  ) => Promise<unknown>;
  /** Offer only people who have activated their account (they sign or approve in the system). */
  onlyActivated: boolean;
  /** Shown in the editor while nobody is chosen. */
  clearWarningKey?: string;
  /** The "clear" link; the default is "— nobody —". */
  clearLabelKey?: string;
  /** The line under the picker; the default says an empty role just skips its step. */
  emptyHintKey?: string;
};

// One row per role, in the order shown on the page.
const ROLES: Role[] = [
  {
    field: "director",
    labelKey: "company_director",
    hintKey: "settings_roles_director_hint",
    pickerLabelKey: "select_director",
    save: updateDirectorSettings,
    onlyActivated: true,
    clearWarningKey: "settings_roles_director_clear_warning",
  },
  {
    field: "boardMember",
    labelKey: "board_member",
    hintKey: "board_member_hint",
    pickerLabelKey: "select_board_member",
    save: updateBoardMemberSettings,
    onlyActivated: true,
  },
  {
    field: "accountancyUser",
    labelKey: "accountancy_user",
    hintKey: "settings_roles_accountant_hint",
    pickerLabelKey: "select_accountancy_user",
    save: updateAccountancyUserSettings,
    onlyActivated: false,
  },
  {
    field: "invoiceApproverUser",
    labelKey: "settings_roles_invoice_approver",
    hintKey: "settings_roles_invoice_approver_hint",
    pickerLabelKey: "select_user",
    save: updateInvoiceApproverSettings,
    onlyActivated: true,
  },
];

// The server's codes for a refused role save, and the text each one gets.
const ROLE_ERROR_KEYS = {
  USER_NOT_ACTIVE_MEMBER: "settings_roles_error_not_member",
  // The assets responsible endpoint's own codes for the same two refusals.
  ASSETS_RESPONSIBLE_USER_NOT_FOUND: "settings_roles_error_not_member",
  COMPANY_USER_MASTER_NOT_FOUND: "settings_master_only",
  USER_REQUIRED: "settings_roles_error_required",
  USER_LACKS_PERMISSION: "settings_roles_error_lacks_permission",
};

/** The name of the person holding a role; the id is all the company record has. */
function HolderName({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState<string | null>(null);

  useEffect(() => {
    let unmounted = false;
    const unknown = t("settings_roles_name_unknown");

    async function loadName() {
      try {
        const { userEntity } = await fetchUserById(userId, companyId);
        if (userEntity.name) return userEntity.name;
        if (userEntity.email) return userEntity.email;
        return unknown;
      } catch {
        return unknown;
      }
    }

    setName(null);
    loadName().then((shown) => {
      if (!unmounted) setName(shown);
    });
    return () => {
      unmounted = true;
    };
  }, [companyId, userId, t]);

  return <>{name}</>;
}

type RoleRowProps = {
  role: Role;
  companyId: string;
  /** Id of the person holding the role, empty when nobody does. */
  holderId: string;
  onRefresh: () => Promise<unknown>;
};

/** One role of a company: who holds it, and an editor to change or clear it. */
export function RoleRow({
  role,
  companyId,
  holderId,
  onRefresh,
}: RoleRowProps) {
  const { t } = useTranslation();
  const edit = useInlineEdit(holderId);
  const { save, saving, error } = useSaveWithToast();

  const saveRole = async () => {
    const saved = await save(
      () =>
        // The server clears the role on null.
        role
          .save({ [role.field]: edit.draft || null }, companyId)
          .then(onRefresh),
      { errorMessage: (err) => t(saveErrorKey(err, ROLE_ERROR_KEYS)) }
    );
    if (saved) edit.cancel();
  };

  return (
    <SettingRow
      label={t(role.labelKey)}
      description={t(role.hintKey)}
      value={
        holderId ? (
          <HolderName companyId={companyId} userId={holderId} />
        ) : (
          <Badge tone="amber">{t("settings_roles_unassigned")}</Badge>
        )
      }
      action={!edit.editing && <ChangeLink onClick={edit.start} />}
    >
      {edit.editing && (
        <EditPanel
          saving={saving}
          error={error}
          onSave={saveRole}
          onCancel={edit.cancel}
        >
          <UserSelect
            value={edit.draft}
            onChange={edit.setDraft}
            label={role.pickerLabelKey}
            onlyActivated={role.onlyActivated}
          />
          <div className="mt-2">
            <ChangeLink
              label={t(role.clearLabelKey ?? "settings_roles_nobody")}
              onClick={() => edit.setDraft("")}
            />
          </div>
          <p className="mt-2 text-xs text-gray-600">
            {t(role.emptyHintKey ?? "settings_roles_may_be_empty")}
          </p>
          {!edit.draft && role.clearWarningKey && (
            <p className="mt-2 text-sm text-amber-700">
              {t(role.clearWarningKey)}
            </p>
          )}
        </EditPanel>
      )}
    </SettingRow>
  );
}

interface ApprovalRolesTabProps {
  companyEntity: CompanyEntity;
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

/** The people the system adds to signing and approvals by itself. */
export default function ApprovalRolesTab({
  companyEntity,
  onRefresh,
}: ApprovalRolesTabProps) {
  return (
    <SettingsCard>
      {ROLES.map((role) => (
        <RoleRow
          key={role.field}
          role={role}
          companyId={companyEntity.id}
          holderId={companyEntity[role.field] || ""}
          onRefresh={onRefresh}
        />
      ))}
    </SettingsCard>
  );
}
